import { Injectable } from '@angular/core';
import { months } from '../shared/utils/date';
import { PeriodoSeleccionadoModal } from '../reporte-ventas-masivo-comercializador/reporte-ventas-comercializadores/modal-periodo/modal-periodo.component';

export type MonthOption = Readonly<{
    name: string;
    number: number;
}>;

export const DEFAULT_MONTHS: readonly MonthOption[] = [
    { name: 'Enero', number: 1 },
    { name: 'Febrero', number: 2 },
    { name: 'Marzo', number: 3 },
    { name: 'Abril', number: 4 },
    { name: 'Mayo', number: 5 },
    { name: 'Junio', number: 6 },
    { name: 'Julio', number: 7 },
    { name: 'Agosto', number: 8 },
    { name: 'Septiembre', number: 9 },
    { name: 'Octubre', number: 10 },
    { name: 'Noviembre', number: 11 },
    { name: 'Diciembre', number: 12 },
];

export type QuarterOption = Readonly<{
    name: string;
    number: number;
}>;

export const DEFAULT_QUARTERS: readonly QuarterOption[] = [
    { name: 'I Trimestre (Ene–Mar)', number: 1 },
    { name: 'II Trimestre (Abr–Jun)', number: 2 },
    { name: 'III Trimestre (Jul–Sep)', number: 3 },
    { name: 'IV Trimestre (Oct–Dic)', number: 4 },
];

export type SemesterOption = Readonly<{
    name: string;
    number: number;
}>;

export const DEFAULT_SEMESTERS: readonly SemesterOption[] = [
    { name: 'I Semestre (Ene–Jun)', number: 1 },
    { name: 'II Semestre (Jul–Dic)', number: 2 },
];

@Injectable({
    providedIn: 'root',
})
export class DateUtilsService {
    /** Equivalente a `new Date(2025, 4, 1)` (1 may 2025, calendario local). */
    readonly MIN_DATE_ISO = '2025-05-01';

    /**
     * Convierte `yyyy-MM-dd` a `Date` en **calendario local** (evita desfaces por zona).
     */
    parseIsoToLocalDate(iso: string): Date {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
        if (!m) {
            return new Date(NaN);
        }
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }

    private toIsoYyyyMmDdFromLocalDate(d: Date): string {
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${mo}-${day}`;
    }

    /**
     * Fecha mínima efectiva como ISO `yyyy-MM-dd`. Entrada `null`/vacío o inválida → {@link MIN_DATE_ISO}.
     */
    getMinDate(date: string | null = null): string {
        if (date === null || date === undefined) {
            return this.MIN_DATE_ISO;
        }
        const trimmed = date.trim();
        if (!trimmed) {
            return this.MIN_DATE_ISO;
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            const d = this.parseIsoToLocalDate(trimmed);
            return Number.isNaN(d.getTime()) ? this.MIN_DATE_ISO : trimmed;
        }
        const d = new Date(trimmed);
        return Number.isNaN(d.getTime())
            ? this.MIN_DATE_ISO
            : this.toIsoYyyyMmDdFromLocalDate(d);
    }

    getCurrentYear(): number {
        return new Date().getFullYear();
    }

    getCurrentMonthNumber(): number {
        return new Date().getMonth() + 1;
    }

    getCurrentQuarterNumber(): number {
        return Math.ceil(this.getCurrentMonthNumber() / 3);
    }

    getCurrentSemesterNumber(): number {
        return this.getCurrentMonthNumber() <= 6 ? 1 : 2;
    }

    buildYearsRange(startYear: number, endYear: number): number[] {
        const from = Math.min(startYear, endYear);
        const to = Math.max(startYear, endYear);
        const years: number[] = [];

        for (let year = to; year >= from; year--) {
            years.push(year);
        }

        return years;
    }

    /**
     * Meses inclusivos entre 1 y 12. Si startMonth > endMonth, se intercambian
     * (mismo criterio que buildYearsRange).
     */
    buildMonthsRange(
        year: number,
        minDate: string | null = null
    ): MonthOption[] {
        const minIso = this.getMinDate(minDate);
        const minD = this.parseIsoToLocalDate(minIso);

        if (year === this.getCurrentYear()) {
            const mesAnterior = this.getCurrentMonthNumber() - 1;
            // Si el año actual ES el mismo año que el de minDate, entonces respetar el mes mínimo
            const startMonth =
                year === minD.getFullYear() ? minD.getMonth() : 0;
            if (mesAnterior > startMonth) {
                return DEFAULT_MONTHS.slice(startMonth, mesAnterior).map(
                    (m) => ({
                        name: m.name,
                        number: m.number,
                    })
                );
            }
            return DEFAULT_MONTHS.slice(0, mesAnterior).map((m) => ({
                name: m.name,
                number: m.number,
            }));
        }
        if (year === minD.getFullYear()) {
            return DEFAULT_MONTHS.slice(minD.getMonth(), 12).map((m) => ({
                name: m.name,
                number: m.number,
            }));
        }
        return DEFAULT_MONTHS.slice(0, 12).map((m) => ({
            name: m.name,
            number: m.number,
        }));
    }

    /**
     * Trimestres disponibles (1–4) según año, fecha mínima y tope al trimestre anterior al actual.
     */
    buildQuartersRange(
        year: number,
        minDate: string | null = null
    ): QuarterOption[] {
        const minIso = this.getMinDate(minDate);
        const minD = this.parseIsoToLocalDate(minIso);
        const minYear = minD.getFullYear();
        const minMonth = minD.getMonth() + 1;
        const minQ = Math.ceil(minMonth / 3);

        const cy = this.getCurrentYear();
        const previousQuarter = this.getCurrentQuarterNumber() - 1;

        if (previousQuarter <= 0 && year === cy) {
            return [];
        }

        let startQ: number;
        let endQ: number;

        if (year === cy) {
            endQ = previousQuarter;
            startQ = year === minYear ? minQ : 1;
        } else if (year < cy) {
            if (year === minYear) {
                startQ = minQ;
                endQ = 4;
            } else {
                startQ = 1;
                endQ = 4;
            }
        } else {
            startQ = year === minYear ? minQ : 1;
            endQ = 4;
        }

        if (startQ > endQ) {
            return [];
        }
        return DEFAULT_QUARTERS.filter(
            (q) => q.number >= startQ && q.number <= endQ
        ).map((q) => ({ name: q.name, number: q.number }));
    }

    /**
     * Semestres disponibles (1–2) según año, fecha mínima y tope al semestre anterior al actual.
     */
    buildSemestersRange(
        year: number,
        minDate: string | null = null
    ): SemesterOption[] {
        const minIso = this.getMinDate(minDate);
        const minD = this.parseIsoToLocalDate(minIso);
        const minYear = minD.getFullYear();
        const minS = minD.getMonth() < 6 ? 1 : 2;

        const cy = this.getCurrentYear();
        const cs = this.getCurrentSemesterNumber();
        const previousSemester = cs - 1;

        if (previousSemester <= 0 && year === cy) {
            return [];
        }

        let startS: number;
        let endS: number;

        if (year === cy) {
            endS = previousSemester;
            startS = year === minYear ? minS : 1;
        } else if (year < cy) {
            if (year === minYear) {
                startS = minS;
                endS = 2;
            } else {
                startS = 1;
                endS = 2;
            }
        } else {
            startS = year === minYear ? minS : 1;
            endS = 2;
        }

        if (startS > endS) {
            return [];
        }
        return DEFAULT_SEMESTERS.filter(
            (s) => s.number >= startS && s.number <= endS
        ).map((s) => ({ name: s.name, number: s.number }));
    }

    getMonthName(monthNumber: number): string {
        const idx = monthNumber - 1;
        if (idx < 0 || idx >= DEFAULT_MONTHS.length) {
            return '';
        }
        return DEFAULT_MONTHS[idx].name;
    }

    getYear(date: Date): number {
        return date.getFullYear();
    }

    getMonth(date: Date): number {
        return date.getMonth() + 1;
    }

    getPeriodName(periodo: string | number | null | undefined): string {
        if (periodo === null || periodo === undefined) return '';

        const raw = String(periodo).trim();
        if (!raw) return '';

        const upper = raw.toUpperCase();

        const roman = (n: number) => {
            switch (n) {
                case 1:
                    return 'I';
                case 2:
                    return 'II';
                case 3:
                    return 'III';
                case 4:
                    return 'IV';
                default:
                    return String(n);
            }
        };

        const trimestreMatch = upper.match(/^T\s*([1-4])$/);
        if (trimestreMatch) {
            const t = Number(trimestreMatch[1]);
            return `${roman(t)} Trimestre`;
        }

        const semestreMatch = upper.match(/^S\s*([1-2])$/);
        if (semestreMatch) {
            const s = Number(semestreMatch[1]);
            return `${roman(s)} Semestre`;
        }

        const asNumber = Number(raw);
        if (Number.isFinite(asNumber) && asNumber >= 1 && asNumber <= 12) {
            const month = months.find((m) => m.number === asNumber);
            return month ? month.name : '';
        }

        return raw;
    }

    computeRange(
        tamano: string,
        seleccion: PeriodoSeleccionadoModal
    ): { start: string; end: string } | null {
        const pad = (n: number) => n.toString().padStart(2, '0');

        if (seleccion.year === null) {
            return null;
        }

        const y = seleccion.year;

        if (tamano === 'Grande') {
            if (!seleccion.month) {
                return null;
            }
            const m = seleccion.month.number;
            console.log('m', m);
            const start = `${y}-${pad(m)}-01`;
            const lastDay = new Date(y, m, 0).getDate();
            const end = `${y}-${pad(m)}-${pad(lastDay)}`;
            return { start, end };
        }

        if (tamano === 'Mediano') {
            if (!seleccion.quarter) {
                return null;
            }
            const q = seleccion.quarter.number;
            const startMonth = (q - 1) * 3 + 1;
            const endMonth = startMonth + 2;
            const start = `${y}-${pad(startMonth)}-01`;
            const lastDay = new Date(y, endMonth, 0).getDate();
            const end = `${y}-${pad(endMonth)}-${pad(lastDay)}`;
            return { start, end };
        }

        if (tamano === 'Pequeño') {
            if (!seleccion.semester) {
                return null;
            }
            const s = seleccion.semester.number;
            if (s === 1) {
                return { start: `${y}-01-01`, end: `${y}-06-30` };
            }
            return { start: `${y}-07-01`, end: `${y}-12-31` };
        }

        return null;
    }
}
